import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  addLeadActivity,
  changeLeadStage,
  closeLead,
  isErpApiSuccess,
  reopenLead,
  showLead,
  updateLead,
} from "../erpClient";
import { getDefaultBranchId } from "../utils/leadBranch";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";

const STAGES = ["NEW", "IN_PROGRESS", "WAITING", "DONE", "LOST"];
const ACTIVITY_TYPES = [
  "NOTE",
  "CALL",
  "SMS",
  "WHATSAPP",
  "VISIT",
  "STATUS_CHANGE",
];

const { tableClass } = {}; // Placeholder for destructuring in render

function formatDt(v) {
  if (v == null || v === "") return "";
  return String(v).replace("T", " ").slice(0, 19);
}

function toDateInput(v) {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  return s;
}

function toDatetimeLocal(v) {
  if (!v) return "";
  const s = String(v).replace(" ", "T").slice(0, 16);
  return s;
}

function fromDatetimeLocal(s) {
  if (!s) return null;
  return s.replace("T", " ") + ":00";
}

function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { buttonClass, cardClass, tableClass } = useButtonTableStyle();
  const branchId = getDefaultBranchId();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStage, setEditStage] = useState("NEW");
  const [editPriority, setEditPriority] = useState(0);
  const [editAssignedTo, setEditAssignedTo] = useState("");
  const [editDueAmount, setEditDueAmount] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editNextFu, setEditNextFu] = useState("");

  const [stageNote, setStageNote] = useState("");
  const [newStage, setNewStage] = useState("IN_PROGRESS");

  const [actType, setActType] = useState("NOTE");
  const [actNote, setActNote] = useState("");
  const [actNextFu, setActNextFu] = useState("");

  const [closeReason, setCloseReason] = useState("");
  const [closeReasonError, setCloseReasonError] = useState("");
  const [showClose, setShowClose] = useState(false);

  const idNum = Number(leadId);
  const isAuto = lead?.origin_mode === "AUTO";

  const load = useCallback(async () => {
    if (!Number.isFinite(idNum)) {
      setLead(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await showLead({ id: idNum, branch_id: branchId });
      if (!isErpApiSuccess(res)) {
        setLead(null);
        toast.error(res?.message || "Lead not found");
        return;
      }
      const data = res.data;
      setLead(data);
      setEditName(data.name || "");
      setEditMobile(data.mobile || "");
      setEditTitle(data.title || "");
      setEditDescription(data.description || "");
      setEditStage(data.stage || "NEW");
      setEditPriority(Number(data.priority) || 0);
      setEditAssignedTo(
        data.assigned_to != null ? String(data.assigned_to) : "",
      );
      setEditDueAmount(
        data.due_amount != null && data.due_amount !== ""
          ? String(data.due_amount)
          : "",
      );
      setEditDueDate(toDateInput(data.due_date));
      setEditNextFu(toDatetimeLocal(data.next_followup_at));
      setNewStage(data.stage || "IN_PROGRESS");
    } catch (err) {
      console.error(err);
      setLead(null);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load lead",
      );
    } finally {
      setLoading(false);
    }
  }, [idNum, branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    if (!lead || isAuto) return;

    const mobileError = getMobileValidationError(editMobile);
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    try {
      const res = await updateLead({
        id: lead.id,
        name: editName,
        mobile: editMobile || null,
        title: editTitle,
        description: editDescription || null,
        stage: editStage,
        priority: Number(editPriority) || 0,
        assigned_to: editAssignedTo.trim() ? Number(editAssignedTo) : null,
        due_amount: editDueAmount.trim() ? Number(editDueAmount) : null,
        due_date: editDueDate || null,
        next_followup_at: editNextFu ? fromDatetimeLocal(editNextFu) : null,
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Update failed");
        return;
      }
      toast.success("Saved");
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Update failed",
      );
    }
  };

  const submitChangeStage = async () => {
    if (!lead) return;
    try {
      const res = await changeLeadStage({
        lead_id: lead.id,
        stage: newStage,
        note: stageNote.trim() || undefined,
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Failed");
        return;
      }
      toast.success("Stage updated");
      setStageNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    }
  };

  const submitActivity = async () => {
    if (!lead) return;
    try {
      const res = await addLeadActivity({
        lead_id: lead.id,
        activity_type: actType,
        note: actNote.trim() || undefined,
        next_followup_at: actNextFu ? fromDatetimeLocal(actNextFu) : null,
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Failed");
        return;
      }
      toast.success("Activity added");
      setActNote("");
      setActNextFu("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    }
  };

  const dismissCloseModal = () => {
    setShowClose(false);
    setCloseReason("");
    setCloseReasonError("");
  };

  const openCloseModal = () => {
    setCloseReason("");
    setCloseReasonError("");
    setShowClose(true);
  };

  const submitClose = async () => {
    if (!lead) return;
    const reason = closeReason.trim();
    if (!reason) {
      setCloseReasonError("Closing reason is required");
      toast.error("Please provide a reason for closing this lead");
      return;
    }
    setCloseReasonError("");
    try {
      const res = await closeLead({
        lead_id: lead.id,
        closed_reason: reason,
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Failed");
        return;
      }
      toast.success("Closed");
      dismissCloseModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    }
  };

  const submitReopen = async () => {
    if (!lead) return;
    try {
      const res = await reopenLead({ lead_id: lead.id });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Failed");
        return;
      }
      toast.success("Reopened");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    }
  };

  if (loading) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom">
              <h4 className="card-title mb-0">Lead details</h4>
            </div>
            <div className="card-body p-4 text-center">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom">
              <h4 className="card-title mb-0">Lead details</h4>
            </div>
            <div className="card-body p-4">
              <p className="text-muted mb-3">Lead not found.</p>
              <Button
                className={buttonClass}
                onClick={() => navigate("/leads")}
              >
                Back to list
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activities = Array.isArray(lead.activities) ? lead.activities : [];

  return (
    <>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            {/* Header with back button and title */}
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${buttonClass}`}
                  onClick={() => navigate("/leads")}
                >
                  ← Back
                </button>
                <h4 className="card-title mb-0">{lead.title || "Lead"} </h4>
              </div>

              <div className="d-flex gap-2">
                {lead.status === "OPEN" ? (
                  <button
                    className={`btn btn-outline-danger ${buttonClass}`}
                    onClick={openCloseModal}
                  >
                    Close
                  </button>
                ) : (
                  <button
                    className={`btn btn-outline-success ${buttonClass}`}
                    onClick={submitReopen}
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>

            <div className="card-body p-4">
              {/* Main content grid */}
              <div className="row g-3">
                {/* Left Column: Details and Basic Info */}
                <div className="col-lg-4">
                  {/* Quick Details Card */}
                  <div className={`card border-0 shadow-sm h-100 ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">Quick Info</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <small className="text-muted d-block">Origin</small>
                        <span className="fw-600 text-dark">
                          {lead.origin_mode}
                        </span>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Type</small>
                        <span className="fw-600 text-dark">
                          {lead.lead_type}
                        </span>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Entity</small>
                        <span className="fw-600 text-dark">
                          {lead.entity_type}{" "}
                          {lead.entity_id != null ? `#${lead.entity_id}` : ""}
                        </span>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Customer</small>
                        <span className="fw-600 text-dark">
                          {lead.customer_id != null
                            ? `#${lead.customer_id}`
                            : "—"}
                        </span>
                      </div>
                      <div className="mb-0">
                        <small className="text-muted d-block">Status</small>
                        <span className="fw-600 text-dark">{lead.status}</span>
                      </div>
                      {lead.closed_reason && (
                        <div className="mt-3 pt-3 border-top">
                          <small className="text-muted d-block">
                            Closed Reason
                          </small>
                          <span className="fw-600 text-danger">
                            {lead.closed_reason}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Middle Column: Edit Form */}
                <div className="col-lg-4">
                  <div className={`card border-0 shadow-sm h-100 ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">
                        {isAuto ? "Information" : "Edit Details"}
                      </h6>
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={isAuto}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">Mobile</Form.Label>
                        <Form.Control
                          {...mobileInputFieldProps}
                          size="sm"
                          value={editMobile}
                          onChange={(e) =>
                            setEditMobile(sanitizeMobileInput(e.target.value))
                          }
                          disabled={isAuto}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">Title</Form.Label>
                        <Form.Control
                          size="sm"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          disabled={isAuto}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">
                          Description
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          disabled={isAuto}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      {!isAuto && (
                        <button
                          className={`btn btn-primary ${buttonClass}`}
                          onClick={saveEdit}
                        >
                          Save changes
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column: Status & Stage */}
                <div className="col-lg-4">
                  <div className={`card border-0 shadow-sm h-100 ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">Status & Details</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Stage
                            </Form.Label>
                            <Form.Select
                              size="sm"
                              value={editStage}
                              onChange={(e) => setEditStage(e.target.value)}
                              disabled={isAuto}
                              className="form-select-sm"
                            >
                              {STAGES.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Priority
                            </Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              min={0}
                              max={255}
                              value={editPriority}
                              onChange={(e) => setEditPriority(e.target.value)}
                              disabled={isAuto}
                              className="form-control-sm"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Assigned To
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              value={editAssignedTo}
                              onChange={(e) =>
                                setEditAssignedTo(e.target.value)
                              }
                              disabled={isAuto}
                              className="form-control-sm"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Due Amount
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              value={editDueAmount}
                              onChange={(e) => setEditDueAmount(e.target.value)}
                              disabled={isAuto}
                              className="form-control-sm"
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Due Date
                            </Form.Label>
                            <Form.Control
                              type="date"
                              size="sm"
                              value={editDueDate}
                              onChange={(e) => setEditDueDate(e.target.value)}
                              disabled={isAuto}
                              className="form-control-sm"
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-600">
                              Next Follow-up
                            </Form.Label>
                            <Form.Control
                              type="datetime-local"
                              size="sm"
                              value={editNextFu}
                              onChange={(e) => setEditNextFu(e.target.value)}
                              disabled={isAuto}
                              className="form-control-sm"
                            />
                          </Form.Group>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Row */}
              <div className="row g-4 mt-2">
                <div className="col-lg-6">
                  <div className={`card border-0 shadow-sm ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">Change Stage</h6>
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-600">
                          New Stage
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={newStage}
                          onChange={(e) => setNewStage(e.target.value)}
                          disabled={lead.status !== "OPEN"}
                          className="form-select-sm"
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">
                          Note (Optional)
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={stageNote}
                          onChange={(e) => setStageNote(e.target.value)}
                          disabled={lead.status !== "OPEN"}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          className={`btn btn-primary ${buttonClass}`}
                          onClick={submitChangeStage}
                          disabled={lead.status !== "OPEN"}
                        >
                          Apply Stage Change
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className={`card border-0 shadow-sm ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">Add Activity</h6>
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-600">Type</Form.Label>
                        <Form.Select
                          size="sm"
                          value={actType}
                          onChange={(e) => setActType(e.target.value)}
                          disabled={lead.status !== "OPEN"}
                          className="form-select-sm"
                        >
                          {ACTIVITY_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-600">Note</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={actNote}
                          onChange={(e) => setActNote(e.target.value)}
                          disabled={lead.status !== "OPEN"}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-600">
                          Set Next Follow-up
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          size="sm"
                          value={actNextFu}
                          onChange={(e) => setActNextFu(e.target.value)}
                          disabled={lead.status !== "OPEN"}
                          className="form-control-sm"
                        />
                      </Form.Group>
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          className={`btn btn-success ${buttonClass}`}
                          onClick={submitActivity}
                          disabled={lead.status !== "OPEN"}
                        >
                          Add Activity
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Timeline - Full Width */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div className={`card border-0 shadow-sm ${cardClass}`}>
                    <div className="px-3 py-2 border-bottom">
                      <h6 className="mb-0 fw-600">
                        Activity Timeline ({activities.length})
                      </h6>
                    </div>
                    <div className="card-body">
                      {activities.length === 0 ? (
                        <p className="text-muted small mb-0">
                          No activities recorded yet.
                        </p>
                      ) : (
                        <div className={`table-responsive border ${cardClass}`}>
                          <table
                            className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                          >
                            <thead className="text-dark fs-4">
                              <tr>
                                <th>Type</th>
                                <th>Note</th>
                                <th>Date Time</th>
                                <th>Next Follow-Up</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activities.map((a) => (
                                <tr key={a.id}>
                                  <td>{a.activity_type}</td>
                                  <td className="text-break">
                                    {a.note || "—"}
                                  </td>
                                  <td>{formatDt(a.created_at)}</td>
                                  <td>
                                    {a.next_followup_at
                                      ? formatDt(a.next_followup_at)
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showClose} onHide={dismissCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Close lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={closeReason}
              isInvalid={!!closeReasonError}
              placeholder="Why is this lead being closed?"
              onChange={(e) => {
                setCloseReason(e.target.value);
                if (closeReasonError) setCloseReasonError("");
              }}
            />
            {closeReasonError ? (
              <div className="invalid-feedback d-block">{closeReasonError}</div>
            ) : null}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissCloseModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitClose}
            disabled={!closeReason.trim()}
          >
            Close lead
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default LeadDetail;
